import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToastUIStore } from '../store/toastUIStore';
import {
  AppError,
  ErrorType,
  createAppError,
  shouldRedirectToLogin,
  getDefaultErrorMessage,
} from '../utils/queryErrorUtils';
import { clearLocalAuthTokens } from '../utils/authHelpers';

/**
 * Type of action to take when an error occurs
 */
export type ErrorAction = 'toast' | 'silent' | 'redirect' | 'custom';

/**
 * Error severity for toast notifications
 */
export type ErrorSeverity = 'error' | 'warning' | 'info';

/**
 * Configuration for error handling
 */
export interface ErrorHandlerConfig {
  /** The type of action to take when an error occurs */
  action?: ErrorAction;

  /** The path to redirect to if action is 'redirect' */
  redirectPath?: string;

  /** Custom handler function if action is 'custom' */
  customHandler?: (error: AppError) => void;

  /** Additional context information to include with the error */
  context?: Record<string, unknown>;

  /** Whether to retry the operation if applicable */
  retry?: boolean;

  /** Error types to exclude from handling */
  excludeErrorTypes?: ErrorType[];

  /** Severity level for toast notifications (defaults to 'error') */
  severity?: ErrorSeverity;

  /** Custom error message to override the default */
  customMessage?: string;

  /** Whether to hide the "Try again" button in toast notifications */
  hideRetryButton?: boolean;

  /** Callback to execute when retry button is clicked */
  onRetry?: () => void;
}

/**
 * Default configuration for different error types
 */
const defaultErrorConfigs: Record<ErrorType, Partial<ErrorHandlerConfig>> = {
  [ErrorType.NETWORK]: {
    action: 'toast',
    retry: true,
    severity: 'error',
  },
  [ErrorType.TIMEOUT]: {
    action: 'toast',
    retry: true,
    severity: 'error',
  },
  [ErrorType.OFFLINE]: {
    action: 'toast',
    retry: false,
    severity: 'warning',
  },
  [ErrorType.AUTH_REQUIRED]: {
    action: 'redirect',
    redirectPath: '/',
    retry: false,
    severity: 'info',
  },
  [ErrorType.AUTH_EXPIRED]: {
    action: 'redirect',
    redirectPath: '/',
    retry: false,
    severity: 'info',
  },
  [ErrorType.PERMISSION_DENIED]: {
    action: 'toast',
    retry: false,
    severity: 'error',
  },
  [ErrorType.QUOTA_EXCEEDED]: {
    action: 'toast',
    retry: false,
    severity: 'warning',
  },
  [ErrorType.NOT_FOUND]: {
    action: 'toast',
    retry: false,
    severity: 'warning',
  },
  [ErrorType.CONFLICT]: {
    action: 'toast',
    retry: true,
    severity: 'warning',
  },
  [ErrorType.VALIDATION]: {
    action: 'toast',
    retry: false,
    severity: 'warning',
  },
  [ErrorType.SERVICE_UNAVAILABLE]: {
    action: 'toast',
    retry: true,
    severity: 'error',
  },
  [ErrorType.DATABASE]: {
    action: 'toast',
    retry: true,
    severity: 'error',
  },
  [ErrorType.UNKNOWN]: {
    action: 'toast',
    retry: true,
    severity: 'error',
  },
};

/**
 * Hook for standardized React Query error handling
 *
 * This hook provides consistent error handling across the app
 * with options for different behaviors based on error type.
 */
export function useQueryErrorHandler() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToastUIStore();

  /**
   * Handle a query or mutation error
   * @param error The error that occurred
   * @param config Configuration for error handling
   * @returns Standardized AppError object
   */
  const handleError = useCallback(
    (error: unknown, config: ErrorHandlerConfig = {}): AppError => {
      // Create standardized error object
      const appError = createAppError(error, config.context);

      // Skip handling for excluded error types
      if (config.excludeErrorTypes?.includes(appError.type)) {
        return appError;
      }

      // Get default config for this error type
      const defaultConfig = defaultErrorConfigs[appError.type];

      // Merge with provided config, with provided config taking precedence
      const finalConfig = {
        ...defaultConfig,
        ...config,
      };

      // Handle authentication errors
      if (shouldRedirectToLogin(appError.type)) {
        // Check if we should prevent recursion (special case for signOut operation)
        const preventRecursion = config.context?.preventRecursion === true;

        // Clear React Query cache
        queryClient.clear();

        // Clear local auth tokens directly instead of using signOut mutation
        // This breaks the circular dependency while still clearing auth state
        clearLocalAuthTokens();

        // Only navigate to login if not already handling a signOut operation
        if (!preventRecursion) {
          // Redirect to landing page which has login functionality
          navigate('/', {
            state: {
              reason: 'session_expired',
              returnTo: window.location.pathname,
            },
          });
        }

        return appError;
      }

      // Handle different action types
      switch (finalConfig.action) {
        case 'toast': {
          // Get message to display
          const message =
            finalConfig.customMessage || appError.message || getDefaultErrorMessage(appError.type);

          // Show toast notification
          addToast({
            type:
              finalConfig.severity === 'warning'
                ? 'warning'
                : finalConfig.severity === 'info'
                  ? 'info'
                  : 'error',
            title: getSeverityTitle(finalConfig.severity || 'error'),
            message,
            autoClose: true,
            showRetry: finalConfig.retry && !finalConfig.hideRetryButton,
            onRetry: finalConfig.onRetry,
            duration: 7000, // Slightly longer duration for error toasts
          });
          break;
        }

        case 'redirect':
          // Redirect to specified path
          if (finalConfig.redirectPath) {
            navigate(finalConfig.redirectPath, {
              state: {
                error: appError.type,
                message: finalConfig.customMessage || appError.message,
              },
            });
          }
          break;

        case 'custom':
          // Use custom handler if provided
          if (finalConfig.customHandler) {
            finalConfig.customHandler(appError);
          }
          break;

        case 'silent':
        default:
          // Don't show error to user, just log it (already done in createAppError)
          break;
      }

      return appError;
    },
    [navigate, queryClient, addToast]
  );

  /**
   * Create a wrapped mutation error handler
   * @param config Error handler configuration
   * @returns Error handler function
   */
  const createMutationErrorHandler = useCallback(
    (config: ErrorHandlerConfig = {}) => {
      return (error: unknown) => handleError(error, config);
    },
    [handleError]
  );

  /**
   * Determine if an error should be retried
   * @param error The error that occurred
   * @returns Boolean indicating if retry is appropriate
   */
  const shouldRetryOperation = useCallback((error: unknown): boolean => {
    const appError = createAppError(error);
    const defaultConfig = defaultErrorConfigs[appError.type];
    return defaultConfig.retry || false;
  }, []);

  /**
   * Get title for toast notification based on severity
   */
  const getSeverityTitle = (severity: ErrorSeverity): string => {
    switch (severity) {
      case 'warning':
        return 'Warning';
      case 'info':
        return 'Information';
      case 'error':
      default:
        return 'Error';
    }
  };

  return {
    handleError,
    createMutationErrorHandler,
    shouldRetryOperation,
  };
}
